// Cómo se le dice al operador el desenlace de una ORDEN MDM: Control MDM la manda sobre UN
// equipo; Cuotas la manda en lote, y por eso el desenlace sigue contando cuántos aceptaron.
// La consulta al proveedor no pasa por aquí: su resultado lo pintan el panel de la ficha y, en
// Cobranza, su propio toast. `MdmExecutableAction` ya no la incluye, así que el tipo lo impide.
import { toast } from 'sonner';
import type { MdmProviderEnum } from '@fiado/type-kit/bin/mdm/enums/MdmProviderEnum';
import { mdmActionErrorMessage } from './actionErrors';
import type { MdmActionOutcome, MdmExecutableAction } from './api/useMdmFleet';
import {
    capitalize,
    describeActionSent,
    describeLockEffective,
    UNPROTECTED_DEVICES_DETAIL,
    unprotectedDevicesTitle,
} from './mdmActions';

/** Cuando el lambda no dice por qué rechazó, el operador igual tiene que leer algo. */
const UNKNOWN_FAILURE = 'El proveedor rechazó la operación.';

const PENDING_NOTE = 'La orden se envió, pero el equipo aún no confirma que la aplicó.';

/** Un SUCCESS que cambia el equipo es la orden aceptada: en Datacultr no confirma que se aplicó. */
const RECEIVED_NOTE: Partial<Record<MdmExecutableAction, string>> = {
    unlock: 'El proveedor recibió la orden; el desbloqueo cuenta cuando la confirme.',
    extendValidity: 'El proveedor recibió la orden; la extensión cuenta cuando la confirme.',
};

export interface MdmOutcomeContext {
    /** La orden que se anuncia. La consulta no es una: no entra en este tipo. */
    key: MdmExecutableAction;
    /** El rótulo del botón que lanzó la orden. */
    label: string;
    /** A quién alcanzó: «IMEI 359…» o «3 cuotas · 2 créditos». */
    scope: string;
    /** Cuántos equipos recibieron la orden. En Control MDM siempre es 1. */
    total: number;
    /** El proveedor del equipo, cuando la orden es sobre uno solo; cambia cómo se anuncia el bloqueo. */
    provider?: MdmProviderEnum;
}

/** «programado» es el bloqueo con corte; lo que solo recibió el proveedor, «enviado». */
function announceAccepted(context: MdmOutcomeContext, result: MdmActionOutcome): void {
    const { key, label, scope, provider } = context;
    const pending = result.pending ?? 0;
    const receivedNote = RECEIVED_NOTE[key];
    if (pending === 0 && receivedNote) {
        toast.info(`${label} · enviado`, {
            description: `${scope} · ${receivedNote} ${capitalize(describeActionSent(key))}`,
        });
        return;
    }
    // Sin hora de corte el bloqueo es una orden aceptada, no un candado programado.
    if (pending === 0 && key === 'lock' && result.lockEffectiveAt === undefined) {
        toast.info(`${label} · enviado`, {
            description: `${scope} · ${describeActionSent(key, undefined, provider)}`,
        });
        return;
    }
    if (pending === 0) {
        toast.success(`${label} · ${key === 'lock' ? 'programado' : 'aplicado'}`, {
            description: `${scope} · ${describeActionSent(key, result.lockEffectiveAt)}`,
        });
        return;
    }
    const applied = result.succeeded - pending;
    const waiting =
        applied > 0
            ? `${String(applied)} de ${String(result.succeeded)} equipos aplicaron la orden; el resto aún no la confirma.`
            : PENDING_NOTE;
    const lock =
        key === 'lock'
            ? ` ${capitalize(describeLockEffective(result.lockEffectiveAt, provider))}.`
            : '';
    toast.info(`${label} · enviado`, { description: `${scope} · ${waiting}${lock}` });
}

/** Toast del 200: el equipo suelto primero, después éxito, fallo total o parcial. */
export function announceMdmOutcome(context: MdmOutcomeContext, result: MdmActionOutcome): void {
    const { label, scope, total } = context;
    if (result.unprotected > 0) {
        // Sin auto-cierre y con botón: el `<Toaster>` de la app no monta uno.
        toast.warning(unprotectedDevicesTitle(result.unprotected), {
            description: UNPROTECTED_DEVICES_DETAIL,
            duration: Infinity,
            closeButton: true,
        });
        return;
    }
    if (result.failed === 0) {
        announceAccepted(context, result);
        return;
    }
    const detail = result.firstErrorMessage ?? UNKNOWN_FAILURE;
    if (result.succeeded === 0) {
        toast.error(`${label} · no se ejecutó`, { description: `${scope} · ${detail}` });
        return;
    }
    toast.warning(`${label} · salió a medias`, {
        description: `${String(result.succeeded)} de ${String(total)} equipos aceptaron la orden. ${detail}`,
    });
}

/** Por qué ningún equipo aceptó la orden, o `null` si alguno la aceptó o quedó suelto. */
export function totalRejectionMessage(result: MdmActionOutcome): string | null {
    if (result.unprotected > 0 || result.succeeded > 0 || result.failed === 0) return null;
    return result.firstErrorMessage ?? UNKNOWN_FAILURE;
}

/** Toast de la orden que ni siquiera obtuvo respuesta. */
export function announceMdmFailure(context: MdmOutcomeContext, error: unknown): void {
    toast.error(`${context.label} · no se ejecutó`, {
        description: `${context.scope} · ${mdmActionErrorMessage(error)}`,
    });
}
